import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { OperationsApiService, type WorkflowApiContract } from '../../../core/api';
import { AuthService } from '../../../core/services/auth.service';
import { CompanyContextService } from '../../../core/tenant/company-context.service';
import { normalizeCoverImageUrl } from '../../../features/projects/adapters/project.adapter';
import {
  readMemberAccessActive,
  readProjectMemberUserId,
} from '../../../features/projects/utils/project-members-display.util';
import { normalizeWorkflowIconKeyForUi } from '../../../shared/utils/workflow-icon-key.util';
import type { SidebarProjectNode, SidebarWorkflowNode } from '../models/sidebar.models';

type JsonRecord = Record<string, unknown>;

function asRecord(value: unknown): JsonRecord {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : {};
}

function asArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  const source = asRecord(value);
  const items = source['items'];
  return Array.isArray(items) ? items : [];
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function firstNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed.length > 0) return trimmed;
  }
  return '';
}

function getInitials(name: string): string {
  const parts = name
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0]?.slice(0, 2) ?? 'PR').toUpperCase();
}

type ProjectMemberAccessRow = {
  userId: string;
  email: string;
  raw: JsonRecord;
  user: JsonRecord;
};

@Injectable({ providedIn: 'root' })
export class SidebarApiService {
  private readonly http = inject(HttpClient);
  private readonly operationsApi = inject(OperationsApiService);
  private readonly auth = inject(AuthService);
  /** Lazy: evita ciclo CompanyContextService → CompanyScopeResetService → SidebarStore → este serviço. */
  private readonly injector = inject(Injector);

  async getSidebarProjects(): Promise<SidebarProjectNode[]> {
    const navigationPayload = await this.tryGetAggregatedSidebar();

    /**
     * Contrato estável: GET /api/navigation/sidebar com `{ projects }` (camelCase).
     * Admin/Owner na empresa: confiamos na lista do servidor (veem todos os projetos expostos).
     * Member: reforçamos com GET /workflows + N× GET …/members — garante que quem foi
     * desativado em `project_members` deixa de ver o projeto mesmo se o aggregate estiver em cache/atrasado.
     *
     * `getProjects()` em paralelo: o navigation endpoint atual não devolve iconKey/colorHex
     * dos projetos (só workflows), então alimentamos esses campos a partir do GET canónico.
     * Sem essa mesclagem, mudar ícone/cor de projeto não reflete na sidebar.
     */
    if (navigationPayload?.trustServerVisibilityFilter) {
      const [apiProjectsForCatalog, treeBase] = await Promise.all([
        this.operationsApi.getProjects().catch(() => []),
        Promise.resolve(navigationPayload.tree),
      ]);
      const projectCatalog = this.buildProjectSidebarCatalog(apiProjectsForCatalog);
      const tree = treeBase.map((node) => this.mergeProjectSidebarDetails(node, projectCatalog));
      if (this.injector.get(CompanyContextService).isAdmin()) {
        return tree;
      }
      const allWorkflows = await this.operationsApi.getWorkflows();
      return this.filterSidebarTreeByActiveMembership(tree, allWorkflows);
    }

    const [apiProjects, allWorkflows] = await Promise.all([
      this.operationsApi.getProjects(),
      this.operationsApi.getWorkflows(),
    ]);

    const coverByProjectId = new Map<string, string | null>();
    for (const p of apiProjects) {
      coverByProjectId.set(p.id, normalizeCoverImageUrl(p.coverImageUrl));
    }

    const workflowCatalog = this.buildWorkflowSidebarCatalog(allWorkflows);

    let tree: SidebarProjectNode[];
    let applyClientMemberFilter: boolean;

    if (navigationPayload !== null) {
      const projectCatalog = this.buildProjectSidebarCatalog(apiProjects);
      const withCovers = navigationPayload.tree.map((node) => ({
        ...node,
        coverImageUrl: this.resolveProjectCoverUrl(node.coverImageUrl, coverByProjectId.get(node.id)),
      }));
      const withProjectDetails = withCovers.map((node) =>
        this.mergeProjectSidebarDetails(node, projectCatalog)
      );
      tree = this.mergeWorkflowSidebarDetailsFromCatalog(withProjectDetails, workflowCatalog);
      applyClientMemberFilter = !navigationPayload.trustServerVisibilityFilter;
    } else {
      const workflowsByProject = new Map<string, SidebarWorkflowNode[]>();
      for (const workflow of allWorkflows) {
        if (!workflow.projectId) continue;
        const bucket = workflowsByProject.get(workflow.projectId) ?? [];
        bucket.push({
          id: workflow.id,
          projectId: workflow.projectId,
          name: workflow.name?.trim() || workflow.id || 'Workflow',
          icon: normalizeWorkflowIconKeyForUi(workflow.iconKey),
          colorHex: this.normalizeWorkflowColorHex(workflow.colorHex) || '#3B82F6',
        });
        workflowsByProject.set(workflow.projectId, bucket);
      }

      const fromOperations = apiProjects
        .map((project) => ({
          id: project.id,
          name: project.name,
          initials: getInitials(project.name),
          coverImageUrl: normalizeCoverImageUrl(project.coverImageUrl),
          iconKey: project.iconKey || null,
          colorHex: this.normalizeWorkflowColorHex(project.colorHex ?? '') || null,
          workflows: (workflowsByProject.get(project.id) ?? []).sort((a, b) =>
            a.name.localeCompare(b.name)
          ),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      tree = this.mergeWorkflowSidebarDetailsFromCatalog(fromOperations, workflowCatalog);
      applyClientMemberFilter = true;
    }

    if (applyClientMemberFilter) {
      return this.filterSidebarTreeByActiveMembership(tree, allWorkflows);
    }
    return tree;
  }

  /** Cor e ícone canónicos a partir de GET /workflows (lista / detalhe usam o mesmo). */
  private buildWorkflowSidebarCatalog(
    workflows: WorkflowApiContract[]
  ): Map<string, { colorHex: string; icon: string }> {
    const map = new Map<string, { colorHex: string; icon: string }>();
    for (const w of workflows) {
      const colorHex = this.normalizeWorkflowColorHex(w.colorHex) || '#3B82F6';
      map.set(w.id, {
        colorHex,
        icon: normalizeWorkflowIconKeyForUi(w.iconKey),
      });
    }
    return map;
  }

  /**
   * Catálogo de iconKey/colorHex/cover dos projetos via GET /api/projects.
   * Necessário porque o /api/navigation/sidebar não devolve esses campos para projetos
   * (só para workflows) — sem esta mesclagem o usuário muda o ícone, o back salva,
   * mas a sidebar continua mostrando iniciais.
   */
  private buildProjectSidebarCatalog(
    projects: Array<{ id: string; iconKey?: string | null; colorHex?: string | null; coverImageUrl?: string | null }>
  ): Map<string, { iconKey: string | null; colorHex: string | null; coverImageUrl: string | null }> {
    const map = new Map<string, { iconKey: string | null; colorHex: string | null; coverImageUrl: string | null }>();
    for (const p of projects) {
      if (!p.id) continue;
      map.set(p.id, {
        iconKey: (p.iconKey ?? '').trim() || null,
        colorHex: this.normalizeWorkflowColorHex(p.colorHex ?? '') || null,
        coverImageUrl: normalizeCoverImageUrl(p.coverImageUrl ?? null),
      });
    }
    return map;
  }

  /**
   * Mescla iconKey/colorHex/cover do catálogo por cima do node do navigation.
   * Catálogo vence quando traz valor — é a fonte canónica desses campos.
   */
  private mergeProjectSidebarDetails(
    node: SidebarProjectNode,
    catalog: Map<string, { iconKey: string | null; colorHex: string | null; coverImageUrl: string | null }>
  ): SidebarProjectNode {
    const detail = catalog.get(node.id);
    if (!detail) return node;
    return {
      ...node,
      iconKey: detail.iconKey ?? node.iconKey ?? null,
      colorHex: detail.colorHex ?? node.colorHex ?? null,
      coverImageUrl: detail.coverImageUrl ?? node.coverImageUrl ?? null,
    };
  }

  private mergeWorkflowSidebarDetailsFromCatalog(
    projects: SidebarProjectNode[],
    catalog: Map<string, { colorHex: string; icon: string }>
  ): SidebarProjectNode[] {
    return projects.map((project) => ({
      ...project,
      workflows: project.workflows.map((wf) => {
        const c = catalog.get(wf.id);
        if (!c) return wf;
        return {
          ...wf,
          colorHex: c.colorHex,
          icon: c.icon,
        };
      }),
    }));
  }

  private normalizeWorkflowColorHex(value: string | null | undefined): string {
    const v = (value ?? '').trim();
    if (/^#[0-9A-Fa-f]{6}$/i.test(v)) {
      return `#${v.slice(1).toUpperCase()}`;
    }
    if (/^#[0-9A-Fa-f]{3}$/i.test(v)) {
      const x = v.slice(1).toUpperCase();
      return `#${x[0]}${x[0]}${x[1]}${x[1]}${x[2]}${x[2]}`;
    }
    return '';
  }

  private resolveProjectCoverUrl(
    fromSidebar: string | null | undefined,
    fromProjectsApi: string | null | undefined
  ): string | null {
    const normalizedSidebar = fromSidebar ? normalizeCoverImageUrl(fromSidebar) : null;
    return normalizedSidebar ?? fromProjectsApi ?? null;
  }

  /**
   * GET /api/navigation/sidebar (ou alias durante rollout).
   * Resposta estável: `{ projects: [{ id, name, coverImageUrl, workflows: [{ id, projectId, name, iconKey, colorHex }] }] }`.
   * Envelope `{ projects }` ⇒ visibilidade resolvida no servidor (`trustServerVisibilityFilter: true`).
   */
  private async tryGetAggregatedSidebar(): Promise<{
    tree: SidebarProjectNode[];
    trustServerVisibilityFilter: boolean;
  } | null> {
    const response = await this.getWithFallback([
      '/api/navigation/sidebar',
      '/api/me/navigation/sidebar',
      '/navigation/sidebar',
    ]);
    if (!response) return null;

    const root = asRecord(response);
    let projectSources: unknown[];
    /** Resposta com chave `projects` (contrato ASP.NET actual) = filtragem feita no back. */
    let trustServerVisibilityFilter: boolean;

    if (Array.isArray(response)) {
      projectSources = response;
      trustServerVisibilityFilter = false;
    } else if (Object.prototype.hasOwnProperty.call(root, 'projects')) {
      projectSources = asArray(root['projects']);
      trustServerVisibilityFilter = true;
    } else {
      projectSources = this.resolveAggregatedProjectSources(root, response);
      trustServerVisibilityFilter = false;
    }

    const tree = this.buildAggregatedSidebarTree(projectSources, root);
    return { tree, trustServerVisibilityFilter };
  }

  private buildAggregatedSidebarTree(projectSources: unknown[], root: JsonRecord): SidebarProjectNode[] {
    const fallbackWorkflows = asArray(root['workflows']).map((workflow) =>
      this.mapAggregatedWorkflow(workflow, null)
    );
    const fallbackByProject = new Map<string, SidebarWorkflowNode[]>();
    for (const workflow of fallbackWorkflows) {
      if (!workflow.projectId) continue;
      const bucket = fallbackByProject.get(workflow.projectId) ?? [];
      bucket.push(workflow);
      fallbackByProject.set(workflow.projectId, bucket);
    }

    return projectSources
      .map((project) => {
        const parsedProject = this.mapAggregatedProject(project);
        const ownWorkflows = asArray(asRecord(project)['workflows']).map((workflow) =>
          this.mapAggregatedWorkflow(workflow, parsedProject.id)
        );
        const fallbackProjectWorkflows = fallbackByProject.get(parsedProject.id) ?? [];
        const workflows = [...ownWorkflows, ...fallbackProjectWorkflows]
          .filter((workflow) => workflow.id)
          .sort((a, b) => a.name.localeCompare(b.name));

        return {
          ...parsedProject,
          workflows,
        };
      })
      .filter((project) => !!project.id && !!project.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private resolveAggregatedProjectSources(root: JsonRecord, raw: unknown): unknown[] {
    const directProjects = asArray(root['projects']);
    if (directProjects.length > 0) return directProjects;

    const dataProjects = asArray(asRecord(root['data'])['projects']);
    if (dataProjects.length > 0) return dataProjects;

    return asArray(raw);
  }

  private mapAggregatedProject(raw: unknown): Omit<SidebarProjectNode, 'workflows'> {
    const source = asRecord(raw);
    const projectData = asRecord(source['project']);
    const id = firstNonEmptyString(source['id'], projectData['id']);
    const name = firstNonEmptyString(
      source['name'],
      source['title'],
      source['projectName'],
      source['nome'],
      projectData['name'],
      projectData['title'],
      projectData['projectName'],
      id
    );
    const iconKey =
      firstNonEmptyString(
        source['iconKey'],
        source['icon'],
        projectData['iconKey'],
        projectData['icon']
      ) || null;
    const colorHex =
      this.normalizeWorkflowColorHex(
        firstNonEmptyString(
          source['colorHex'],
          source['color_hex'],
          source['accentColor'],
          source['color'],
          projectData['colorHex'],
          projectData['color_hex'],
          projectData['accentColor'],
          projectData['color']
        )
      ) || null;
    return {
      id,
      name,
      initials: getInitials(name),
      coverImageUrl:
        asNullableString(source['coverImageUrl']) ??
        asNullableString(source['coverImage']) ??
        asNullableString(source['logoUrl']) ??
        asNullableString(source['assetUrl']) ??
        asNullableString(source['brandLogoUrl']) ??
        asNullableString(projectData['coverImageUrl']) ??
        asNullableString(projectData['logoUrl']) ??
        asNullableString(projectData['assetUrl']),
      iconKey,
      colorHex,
    };
  }

  private mapAggregatedWorkflow(raw: unknown, projectIdFallback: string | null): SidebarWorkflowNode {
    const source = asRecord(raw);
    const workflowData = asRecord(source['workflow']);
    const id = firstNonEmptyString(source['id'], workflowData['id']);
    const projectId = firstNonEmptyString(
      source['projectId'],
      source['projectID'],
      workflowData['projectId'],
      workflowData['projectID'],
      projectIdFallback
    );
    const name = firstNonEmptyString(
      source['name'],
      source['title'],
      source['workflowName'],
      source['listName'],
      source['nome'],
      workflowData['name'],
      workflowData['title'],
      id
    );

    const iconRaw = firstNonEmptyString(
      source['iconKey'],
      source['icon'],
      workflowData['iconKey'],
      workflowData['icon']
    );

    const colorRaw = firstNonEmptyString(
      source['colorHex'],
      source['color_hex'],
      source['accentColor'],
      source['color'],
      workflowData['colorHex'],
      workflowData['color_hex'],
      workflowData['accentColor'],
      workflowData['color']
    );

    return {
      id,
      projectId,
      name: name || 'Workflow',
      icon: normalizeWorkflowIconKeyForUi(iconRaw || undefined),
      colorHex: this.normalizeWorkflowColorHex(colorRaw) || '#3B82F6',
    };
  }

  /**
   * Fallback (sem sidebar agregada com `{ projects }`): filtro no cliente.
   * Alinhado com a API quando possível: Admin/Owner sem recorte; Member com project_members
   * ativo e workflows restritos só com workflow_members (N+1 evitado quando o servidor filtra o menu).
   */
  private async filterSidebarTreeByActiveMembership(
    projects: SidebarProjectNode[],
    allWorkflows: WorkflowApiContract[]
  ): Promise<SidebarProjectNode[]> {
    if (!projects.length) return [];
    if (this.injector.get(CompanyContextService).isAdmin()) {
      return projects;
    }

    const session = this.auth.currentUser();
    const uid = (session?.id || '').trim().toLowerCase();
    const uemail = (session?.email || '').trim().toLowerCase();
    if (!uid && !uemail) {
      return projects;
    }

    const wfById = new Map(allWorkflows.map((w) => [w.id, w]));

    const projectRowsList = await Promise.all(
      projects.map((p) => this.fetchProjectMemberAccessRows(p.id))
    );

    const memberVisibleProjects: SidebarProjectNode[] = [];
    for (let i = 0; i < projects.length; i++) {
      const p = projects[i];
      const rows = projectRowsList[i];
      if (rows === null) {
        memberVisibleProjects.push(p);
        continue;
      }
      const me = this.findProjectMemberRowForSession(rows, uid, uemail);
      if (me && readMemberAccessActive(me.raw, me.user)) {
        memberVisibleProjects.push(p);
      }
    }

    const restrictedWorkflowIds = new Set<string>();
    for (const p of memberVisibleProjects) {
      for (const wf of p.workflows) {
        const w = wfById.get(wf.id);
        if (w?.isAccessRestricted) {
          restrictedWorkflowIds.add(wf.id);
        }
      }
    }

    const restrictedPresence = new Map<string, boolean | null>();
    await Promise.all(
      [...restrictedWorkflowIds].map(async (wid) => {
        const v = await this.fetchWorkflowHasSessionMember(wid, uid, uemail);
        restrictedPresence.set(wid, v);
      })
    );

    return memberVisibleProjects.map((p) => ({
      ...p,
      workflows: p.workflows.filter((wf) => {
        const w = wfById.get(wf.id);
        if (!w || !w.isAccessRestricted) {
          return true;
        }
        const pr = restrictedPresence.get(wf.id);
        if (pr === null) {
          return true;
        }
        return pr === true;
      }),
    }));
  }

  /** `null` = falha ao carregar; não escondemos o projeto por erro transitório. */
  private async fetchProjectMemberAccessRows(
    projectId: string
  ): Promise<ProjectMemberAccessRow[] | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<unknown>(`/api/projects/${projectId}/members`)
      );
      const items = this.parseProjectMembersResponse(response);
      return items.map((item) => this.mapProjectMemberAccessRow(item));
    } catch {
      return null;
    }
  }

  private parseProjectMembersResponse(raw: unknown): JsonRecord[] {
    if (Array.isArray(raw)) {
      return raw.map((x) => asRecord(x));
    }
    const root = asRecord(raw);
    const items = asArray(root['items']);
    if (items.length) {
      return items.map((x) => asRecord(x));
    }
    return [
      ...asArray(root['members']).map((x) => asRecord(x)),
      ...asArray(root['users']).map((x) => asRecord(x)),
      ...asArray(root['entries']).map((x) => asRecord(x)),
    ];
  }

  private mapProjectMemberAccessRow(item: JsonRecord): ProjectMemberAccessRow {
    const user = asRecord(item['user']);
    const userId = readProjectMemberUserId(item, user).trim();
    const email = firstNonEmptyString(
      asNullableString(item['email']),
      asNullableString(user['email'])
    ).toLowerCase();
    return { userId, email, raw: item, user };
  }

  private findProjectMemberRowForSession(
    rows: ProjectMemberAccessRow[],
    uid: string,
    uemail: string
  ): ProjectMemberAccessRow | null {
    const normGuid = (s: string) => s.trim().toLowerCase().replace(/[{}]/g, '');
    const uidNorm = uid ? normGuid(uid) : '';
    for (const row of rows) {
      if (uidNorm && row.userId) {
        const rowId = normGuid(row.userId);
        if (rowId && rowId === uidNorm) {
          return row;
        }
      }
      if (uemail && row.email && row.email === uemail) {
        return row;
      }
    }
    return null;
  }

  /** `true` se o utilizador está na lista; `false` se não está; `null` se o GET falhou. */
  private async fetchWorkflowHasSessionMember(
    workflowId: string,
    uid: string,
    uemail: string
  ): Promise<boolean | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<unknown>(`/api/workflows/${workflowId}/members`)
      );
      const items = Array.isArray(response)
        ? response.map((x) => asRecord(x))
        : asArray(asRecord(response)['items']).map((x) => asRecord(x));
      for (const item of items) {
        if (this.workflowMemberRowMatchesSession(item, uid, uemail)) {
          return true;
        }
      }
      return false;
    } catch {
      return null;
    }
  }

  private workflowMemberRowMatchesSession(item: JsonRecord, uid: string, uemail: string): boolean {
    const normGuid = (s: string) => s.trim().toLowerCase().replace(/[{}]/g, '');
    const user = asRecord(item['user']);
    const rowUserId = firstNonEmptyString(
      item['userId'],
      item['UserId'],
      user['id'],
      user['Id'],
      user['userId'],
      user['UserId']
    );
    const email = firstNonEmptyString(
      item['email'],
      item['Email'],
      asNullableString(user['email']) ?? '',
      asNullableString(user['Email']) ?? ''
    ).toLowerCase();
    if (uid && rowUserId) {
      const a = normGuid(uid);
      const b = normGuid(rowUserId);
      if (a && b && a === b) {
        return true;
      }
    }
    if (uemail && email && email === uemail) {
      return true;
    }
    return false;
  }

  private async getWithFallback(endpoints: string[]): Promise<unknown | null> {
    let lastError: unknown;
    for (const endpoint of endpoints) {
      try {
        return await firstValueFrom(this.http.get<unknown>(endpoint));
      } catch (error) {
        lastError = error;
      }
    }

    if (lastError) return null;
    return null;
  }
}
